import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { orderBy } from 'natural-orderby';
import queryString from 'query-string';
import { useLocation, useParams } from 'react-router-dom';
import { getDataPromise, putDataPromise } from 'brock-fetch-utils';
import {
    PERSISTED_LINK,
    EXTERNAL_LINK,
    decompressEncodedURLToConfiguration,
    SEARCH_LINK,
    noLinksExist,
    multipleLinksExist,
    SAVED_SEARCH_NAME,
} from './searchLinkUtils';
import { setFiltersBasedOnLanguage } from '../viewDashboards/dashboardUtils';
import { sanitizeTimeRange } from './configurationAndValidationUtils';
import {
    parseUserPreferenceValue,
    getUserPreferenceKey,
} from '../shared/userPreferenceUtils';
import {
    getKeyFromSavedSearchName,
    getsearchStateFromQueryParams,
} from './searchUtils';
import { useLabelContext } from '../shared/hooks/labelContext';

function getSavedStateForPersistence(key, stateToPersist) {
    return {
        key,
        value: JSON.stringify(stateToPersist),
    };
}

function addKeyAndNameToSavedSearch(savedSearch) {
    return {
        ...savedSearch.value,
        key: savedSearch.key,
        name: savedSearch.name,
    };
}

function appendDefaultSavedSearch(savedSearches, searchLabels) {
    const defaultSavedSearch = {
        name: searchLabels.default,
        key: 'Default',
    };

    return [defaultSavedSearch, ...savedSearches];
}

function getSharedUrlById(uriConstants, id) {
    return getDataPromise(uriConstants.getSharedUrl(id)).then(
        (response) => response.payload
    );
}

async function getSharedUrlResponse(queryParams, uriConstants) {
    let sharedUrlResponse;

    if (queryParams[PERSISTED_LINK]) {
        sharedUrlResponse = await getSharedUrlById(
            uriConstants,
            queryParams[PERSISTED_LINK]
        );
    } else if (queryParams[EXTERNAL_LINK]) {
        sharedUrlResponse = queryParams[EXTERNAL_LINK];
    } else {
        sharedUrlResponse = queryParams[SEARCH_LINK];
    }

    return sharedUrlResponse;
}

/* eslint-disable max-params, max-statements, complexity */
async function appendExternalLinkConfiguration(
    initialConfiguration,
    savedSearches,
    queryParams,
    labels,
    uriConstants,
    selectOptions
) {
    let hydratedConfiguration;

    try {
        if (
            isEmpty(queryParams) ||
            multipleLinksExist(queryParams) ||
            queryParams[SAVED_SEARCH_NAME]
        ) {
            return savedSearches;
        }

        if (!isEmpty(queryParams) && noLinksExist(queryParams)) {
            const {
                urlTimeRange,
                urlFilterValues,
                urlWhereClauses,
                urlRecordLimit,
            } = getsearchStateFromQueryParams(
                queryParams,
                initialConfiguration.filterConfigurations,
                initialConfiguration.whereClauseConfiguration
                    .whereClauseOptions,
                selectOptions,
                labels.predicateLabels
            );

            hydratedConfiguration = {
                timeAndPrecision: {
                    ...initialConfiguration.timeAndPrecision,
                    ...urlTimeRange,
                },
                filterValues: {
                    ...initialConfiguration.filterValues,
                    ...urlFilterValues,
                },
                whereClauses: [
                    ...initialConfiguration.whereClauses,
                    ...urlWhereClauses,
                ],
                recordLimit:
                    urlRecordLimit.recordLimit ||
                    initialConfiguration.recordLimit,
                isDescending:
                    !!urlRecordLimit.isDescending ||
                    !!initialConfiguration.isDescending,
            };
        } else {
            const sharedUrlResponse = await getSharedUrlResponse(
                queryParams,
                uriConstants
            );
            if (!sharedUrlResponse) {
                return savedSearches;
            }

            hydratedConfiguration = await decompressEncodedURLToConfiguration(
                sharedUrlResponse
            );

            setFiltersBasedOnLanguage(
                hydratedConfiguration,
                selectOptions,
                initialConfiguration.filterConfigurations,
                labels.predicateLabels
            );

            if (queryParams[SEARCH_LINK]) {
                hydratedConfiguration = {
                    timeAndPrecision: {
                        ...initialConfiguration.timeAndPrecision,
                        ...hydratedConfiguration.timeAndPrecision,
                    },
                    filterValues: {
                        ...initialConfiguration.filterValues,
                        ...hydratedConfiguration.filterValues,
                    },
                    whereClauses: [
                        ...initialConfiguration.whereClauses,
                        ...hydratedConfiguration.whereClauses,
                    ],
                };
            }
        }

        const externalLinkSearch = {
            name: labels.labels.searchLabels.sharedSearch,
            key: '',
            ...hydratedConfiguration,
        };

        return [externalLinkSearch, ...savedSearches];
    } catch (error) {
        /* eslint-disable no-alert, prefer-destructuring */
        alert(labels.labels.searchLabels.externalLinkErrorMessage);
        window.location.href = window.location.href.split('?')[0];
        throw error;
        /* eslint-enable */
    }
}
/* eslint-enable */

export default function ExternalState(props) {
    const labels = useLabelContext();
    const location = useLocation();
    const params = useParams();

    const [isLoaded, setIsLoaded] = useState(false);
    const [savedSearches, setSavedSearches] = useState([]);
    const [loadedSavedSearch, setLoadedSearch] = useState({});
    const [savedSearchError, setSavedSearchError] = useState(null);
    const [userPreferences, setUserPreferences] = useState({
        savedSearches: [],
    });

    const setLoadedSavedSearch = useCallback((configuration) => {
        setLoadedSearch({
            ...configuration,
            timeAndPrecision: sanitizeTimeRange(
                configuration.timeAndPrecision,
                configuration.timeAndPrecisionConfiguration.predefinedDateRanges
            ),
        });
    }, []);

    const onGetSavedSearchError = useCallback(() => {
        const defaultSearches = appendDefaultSavedSearch(
            [],
            labels.searchLabels
        );

        setSavedSearchError(labels.searchLabels.errorRetrievingSavedSearches);
        setSavedSearches(defaultSearches);
        setLoadedSavedSearch({
            ...props.initialConfiguration,
            ...defaultSearches[0],
        });
        setIsLoaded(true);
    }, [setLoadedSavedSearch, labels.searchLabels, props.initialConfiguration]);

    /* eslint-disable complexity */
    useEffect(
        () => {
            if (props.initialStateLoaded) {
                getDataPromise(
                    props.uriConstants.getUserPreferences(
                        getUserPreferenceKey(params.searchKey)
                    )
                ).then(async (response) => {
                    const parsedPreferences =
                        parseUserPreferenceValue(response);

                    const parsedSavedSearches = orderBy(
                        parsedPreferences.savedSearches.map(
                            addKeyAndNameToSavedSearch
                        ),
                        ['name']
                    );

                    const savedSearchesWithDefault = appendDefaultSavedSearch(
                        parsedSavedSearches,
                        labels.searchLabels
                    );

                    const queryParams = queryString.parse(location.search);
                    const savedSearchesWithExternalLink =
                        await appendExternalLinkConfiguration(
                            props.initialConfiguration,
                            savedSearchesWithDefault,
                            queryParams,
                            labels,
                            props.uriConstants,
                            props.selectOptions
                        );

                    let searchToLoad = savedSearchesWithExternalLink[0];

                    const savedSearchName =
                        queryParams[SAVED_SEARCH_NAME] ??
                        props.initialConfiguration.key;

                    if (savedSearchName) {
                        const savedSearch = savedSearchesWithExternalLink.find(
                            (x) => x.key === savedSearchName
                        );

                        if (savedSearch) {
                            searchToLoad = savedSearch;
                        }
                    }

                    setSavedSearches(savedSearchesWithExternalLink);
                    setLoadedSavedSearch({
                        ...props.initialConfiguration,
                        ...searchToLoad,
                    });
                    setIsLoaded(true);
                    setSavedSearchError(null);
                    setUserPreferences(parsedPreferences);
                }, onGetSavedSearchError);
            }
        },
        /* eslint-disable react-hooks/exhaustive-deps */
        [
            params.searchKey,
            props.initialStateLoaded,
            props.uriConstants,
            props.location,
            labels,
            props.initialConfiguration,
            props.selectOptions,
            onGetSavedSearchError,
        ]
        /* eslint-enable react-hooks/exhaustive-deps */
    );

    const saveSearch = (name, isSaveNew, stateToPersist) => {
        const userPreferenceKey = getUserPreferenceKey(params.searchKey);

        // prettier-ignore
        const key = isSaveNew
            ? `${userPreferenceKey}_${getKeyFromSavedSearchName(name)}`
            : name;

        /* eslint-disable indent */
        const SearchName = isSaveNew
            ? name
            : savedSearches.find((savedSearch) => savedSearch.key === name)
                  .name;
        /* eslint-enable indent */

        let preferences;
        if (isSaveNew) {
            preferences = {
                ...userPreferences,
                savedSearches: [
                    ...userPreferences.savedSearches,
                    {
                        key,
                        name: SearchName,
                        value: stateToPersist,
                    },
                ],
            };
        } else {
            const savedSearchToUpdate = userPreferences.savedSearches.find(
                (x) => x.key === key
            );

            const otherSavedSearches = userPreferences.savedSearches.filter(
                (x) => x.key !== key
            );

            preferences = {
                ...userPreferences,
                savedSearches: [
                    ...otherSavedSearches,
                    {
                        ...savedSearchToUpdate,
                        value: stateToPersist,
                    },
                ],
            };
        }

        putDataPromise(
            props.uriConstants.saveUserPreferences,
            getSavedStateForPersistence(userPreferenceKey, preferences)
        ).then(
            () =>
                getDataPromise(
                    props.uriConstants.getUserPreferences(
                        getUserPreferenceKey(params.searchKey)
                    )
                ).then((response) => {
                    const parsedPreferences =
                        parseUserPreferenceValue(response);

                    const savedSearchesWithDefault = appendDefaultSavedSearch(
                        parsedPreferences.savedSearches.map(
                            addKeyAndNameToSavedSearch
                        ),
                        labels.searchLabels
                    );

                    const savedSearchToLoad = savedSearchesWithDefault.find(
                        (savedSearch) => savedSearch.key === key
                    );

                    setSavedSearches(savedSearchesWithDefault);
                    setLoadedSavedSearch({
                        ...props.initialConfiguration,
                        ...savedSearchToLoad,
                    });
                    setSavedSearchError(null);
                    setUserPreferences(parsedPreferences);
                }, onGetSavedSearchError),
            () => {
                setSavedSearchError(labels.searchLabels.errorSavingSearch);
            }
        );
    };

    const deleteSavedSearch = (key) => {
        const userPreferenceKey = getUserPreferenceKey(params.searchKey);

        const preferences = {
            ...userPreferences,
            savedSearches: userPreferences.savedSearches.filter(
                (x) => x.key !== key
            ),
        };

        putDataPromise(
            props.uriConstants.saveUserPreferences,
            getSavedStateForPersistence(userPreferenceKey, preferences)
        ).then(
            () =>
                getDataPromise(
                    props.uriConstants.getUserPreferences(
                        getUserPreferenceKey(params.searchKey)
                    )
                ).then(
                    (response) => {
                        const parsedPreferences =
                            parseUserPreferenceValue(response);

                        const savedSearchesWithDefault =
                            appendDefaultSavedSearch(
                                parsedPreferences.savedSearches.map(
                                    addKeyAndNameToSavedSearch
                                ),
                                labels.labels.searchLabels
                            );

                        setUserPreferences(parsedPreferences);
                        setSavedSearches(savedSearchesWithDefault);
                        setSavedSearchError(null);

                        if (key === loadedSavedSearch.key) {
                            const savedSearchToLoad =
                                savedSearchesWithDefault.find(
                                    (savedSearch) =>
                                        savedSearch.key === 'Default'
                                );

                            setLoadedSavedSearch({
                                ...props.initialConfiguration,
                                ...savedSearchToLoad,
                            });
                        }
                    },
                    () => onGetSavedSearchError()
                ),
            () => {
                setSavedSearchError(
                    labels.labels.searchLabels.errorDeletingSavedSearch
                );
            }
        );
    };

    /* eslint-disable complexity */
    const loadSavedSearch = (key) => {
        const savedSearchToLoad = savedSearches.find(
            (savedSearch) => savedSearch.key === key
        );

        setFiltersBasedOnLanguage(
            savedSearchToLoad,
            props.selectOptions,
            props.initialConfiguration.filterConfigurations,
            labels.predicateLabels
        );

        const defaultFilterValues =
            key === 'Default' ? { filterValues: {} } : {};

        setLoadedSavedSearch({
            ...props.initialConfiguration,
            ...defaultFilterValues,
            ...savedSearchToLoad,
        });
    };
    /* eslint-enable */
    return props.render({
        deleteSavedSearch,
        isLoaded: props.initialStateLoaded && isLoaded,
        loadedSavedSearch,
        loadSavedSearch,
        saveSearch,
        savedSearchError,
        savedSearchNames: savedSearches.map((savedSearch) => ({
            name: savedSearch.name,
            key: savedSearch.key,
        })),
    });
}

ExternalState.propTypes = {
    initialConfiguration: PropTypes.instanceOf(Object),
    userPreferences: PropTypes.instanceOf(Object),
    initialStateLoaded: PropTypes.bool.isRequired,
    labels: PropTypes.instanceOf(Object),
    render: PropTypes.func.isRequired,
    selectOptions: PropTypes.instanceOf(Array).isRequired,
    uriConstants: PropTypes.instanceOf(Object).isRequired,
};

ExternalState.defaultProps = {
    initialConfiguration: {},
};
